import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Clock, Loader2, Trophy } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { ASSETS } from "@/config/assets";

function useCountdown(target) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = new Date(target).getTime() - Date.now();
      if (diff <= 0) { setLeft("00:00:00"); return; }
      const h = Math.floor(diff / 3.6e6);
      const m = Math.floor((diff % 3.6e6) / 6e4);
      const s = Math.floor((diff % 6e4) / 1000);
      setLeft(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return left;
}

export function CarrotGame() {
  const [state, setState] = useState(null);
  const [playing, setPlaying] = useState(false);
  const countdown = useCountdown(state?.next_reset);

  const load = () => api.get("/game/daily").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);

  const opened = state?.opened || [];
  const completed = !!state?.completed;

  const play = async (i) => {
    if (completed || opened.includes(i) || playing) return;
    setPlaying(true);
    try {
      const { data } = await api.post("/game/daily/play", { box_index: i });
      setState((s) => ({ ...s, ...data }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
    } finally {
      setPlaying(false);
    }
  };

  if (!state) return <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-green-400" /></div>;

  return (
    <div data-testid="carrot-game" className="rounded-2xl border-2 border-green-500/40 bg-zinc-900 p-4">
      <p className="font-display text-lg font-black uppercase leading-tight text-white">
        Trova il <span className="text-green-400">premio</span>
      </p>
      <p className="mb-3 text-xs font-semibold text-zinc-400">
        Clicca sulle tre scatole e cerca di non trovare Tony, ma solo il premio!
      </p>

      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => {
          const isOpened = opened.includes(i);
          const pos = opened.indexOf(i);
          const isBonusBox = isOpened && pos === 2;
          let cls = "border-zinc-700 bg-zinc-950";
          if (!isOpened && !completed) cls = "cursor-pointer border-green-500 bg-zinc-950 hover:scale-105 hover:bg-zinc-800";
          if (isBonusBox) cls = "border-green-400 bg-green-500/20";
          return (
            <button
              key={i}
              data-testid={`carrot-box-${i}`}
              disabled={completed || isOpened || playing}
              onClick={() => play(i)}
              className={`relative flex aspect-square items-center justify-center overflow-hidden rounded-2xl border-2 transition-all disabled:cursor-not-allowed ${cls}`}
            >
              {!isOpened ? (
                <Gift className="h-8 w-8 text-green-400" />
              ) : isBonusBox ? (
                <Trophy className="h-9 w-9 text-green-400" />
              ) : (
                <img data-testid={`carrot-tony-${i}`} src={ASSETS.carrotTony} alt="Tony" className="h-full w-full object-cover" draggable={false} />
              )}
            </button>
          );
        })}
      </div>

      {!completed && opened.length > 0 && (
        <p data-testid="carrot-progress" className="mt-3 text-center text-xs font-bold text-zinc-300">
          Hai aperto {opened.length}/3 scatole — continua a cercare il premio!
        </p>
      )}

      {completed && (
        <div data-testid="carrot-result-win" className="mt-4">
          <p className="text-center font-display text-base font-black uppercase text-green-400">Hai trovato il premio del giorno! 🎉</p>
          {state.bonus?.image_path && <img src={fileUrl(state.bonus.image_path)} alt="" className="mt-3 w-full rounded-2xl object-cover" />}
          {state.bonus?.cta_url && (
            <a data-testid="carrot-bonus-cta" href={state.bonus.cta_url} target="_blank" rel="noopener noreferrer"
              className="mt-3 flex w-full items-center justify-center rounded-full bg-green-500 py-3 text-sm font-black uppercase tracking-wide text-zinc-950 transition-colors hover:bg-green-400">
              {state.bonus.cta_text || "Scopri il bonus"}
            </a>
          )}
          {!state.bonus?.image_path && !state.bonus?.cta_url && (
            <p className="mt-2 text-center text-xs font-semibold text-zinc-400">Il bonus di oggi arriva a breve, resta connesso!</p>
          )}
        </div>
      )}

      {completed && (
        <p data-testid="carrot-countdown" className="mt-3 flex items-center justify-center gap-1.5 text-xs font-semibold text-zinc-400">
          <Clock className="h-3.5 w-3.5 text-green-400" /> Prossimo tentativo tra <span className="font-mono-t text-green-400">{countdown}</span>
        </p>
      )}
    </div>
  );
}
